import type { Trimester } from './types';

export interface SelectOption {
  value: string;
  label: string;
}

export interface BiometryField {
  key: string;
  label: string;
  unit?: string;
  placeholder?: string;
  showPercentile?: boolean;
}

export interface MorphologyItem {
  key: string;
  label: string;
}

export interface SelectFieldDef {
  key: string;
  label: string;
  defaultValue: string;
  options: SelectOption[];
}

export interface NumberFieldDef {
  key: string;
  label: string;
  placeholder?: string;
  step?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
}

export type FieldDef = SelectFieldDef | NumberFieldDef;

export function isSelectField(f: FieldDef): f is SelectFieldDef {
  return 'options' in f;
}

// ─── Reusable option sets ────────────────────────────────────────

export const OPTIONS = {
  presentAbsent: [
    { value: 'present', label: 'options.present' },
    { value: 'absent', label: 'options.absent' },
  ],
  yesNo: [
    { value: 'yes', label: 'options.yes' },
    { value: 'no', label: 'options.no' },
  ],
  positiveNegative: [
    { value: 'negative', label: 'options.negative' },
    { value: 'positive', label: 'options.positive' },
    { value: 'not-done', label: 'options.notDone' },
  ],
  morphologyStatus: [
    { value: 'normal', label: 'options.normal' },
    { value: 'abnormal', label: 'options.abnormal' },
    { value: 'not-seen', label: 'options.notSeen' },
  ],
  cardiacActivity: [
    { value: 'present-regular', label: 'options.presentRegular' },
    { value: 'irregular', label: 'options.irregular' },
    { value: 'absent', label: 'options.absent' },
  ],
  baselineFhr: [
    { value: 'BRADYCARDIA', label: 'Bradycardia' },
    { value: 'NORMAL', label: 'options.normal' },
    { value: 'TACHYCARDIA', label: 'Tachycardia' },
  ],
  fetalMovement: [
    { value: 'active', label: 'options.activeNormal' },
    { value: 'reduced', label: 'options.reduced' },
    { value: 'absent', label: 'options.absent' },
  ],
  fetalPresentation: [
    { value: 'cephalic', label: 'options.vertexCephalic' },
    { value: 'breech', label: 'options.breech' },
    { value: 'transverse', label: 'options.transverse' },
    { value: 'oblique', label: 'options.oblique' },
    { value: 'undetermined', label: 'options.undetermined' },
    { value: 'unknown', label: 'options.unknown' },
  ],
  facing: [
    { value: 'left', label: 'options.left' },
    { value: 'right', label: 'options.right' },
    { value: 'unknown', label: 'options.unknown' },
  ],
  diastolicFlow: [
    { value: 'PRESENT', label: 'options.present' },
    { value: 'ABSENT', label: 'options.absentAedf' },
    { value: 'REVERSED', label: 'options.reversedRedf' },
  ],
  aWave: [
    { value: 'POSITIVE', label: 'options.positiveNormal' },
    { value: 'ABSENT', label: 'options.absent' },
    { value: 'REVERSED', label: 'options.reversed' },
  ],
  funneling: [
    { value: 'absent', label: 'options.absent' },
    { value: 'present', label: 'options.present' },
  ],
  amnioticFluid: [
    { value: 'NORMAL', label: 'options.normal' },
    { value: 'OLIGOHYDRAMNIOS', label: 'options.oligohydramnios' },
    { value: 'POLYHYDRAMNIOS', label: 'options.polyhydramnios' },
  ],
  placentalLocation: [
    { value: 'fundal', label: 'options.fundal' },
    { value: 'anterior', label: 'options.anterior' },
    { value: 'posterior', label: 'options.posterior' },
    { value: 'lateral', label: 'options.lateral' },
    { value: 'placenta_previa', label: 'options.placentaPrevia' },
    { value: 'undetermined', label: 'options.undetermined' },
  ],
  placentaCervixClass: [
    { value: 'normal', label: 'options.normalGt2cm' },
    { value: 'low-lying', label: 'options.lowLying' },
    { value: 'previa', label: 'options.previa' },
  ],
  grannumGrade: [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
  ],
  cordVessels: [
    { value: '3v', label: 'options.threeVesselsNormal' },
    { value: 'sua', label: 'options.suaSingleArtery' },
  ],
  examConditions: [
    { value: 'good', label: 'options.good' },
    { value: 'fair', label: 'options.fair' },
    { value: 'poor', label: 'options.poor' },
  ],
  indications: [
    { value: 'routine', label: 'options.routineScreening' },
    { value: 'dating', label: 'options.datingScan' },
    { value: 'morphology', label: 'options.morphologyScan' },
    { value: 'iugr', label: 'options.iugrSurveillance' },
    { value: 'anomaly', label: 'options.anomalyFollowUp' },
    { value: 'cervical', label: 'options.cervicalLength' },
    { value: 'wellbeing', label: 'options.fetalWellbeing' },
    { value: 'presentation', label: 'options.presentation' },
  ],
  chorionicity: [
    { value: 'singleton', label: 'options.singleton' },
    { value: 'dc-da', label: 'options.dcDa' },
    { value: 'mc-da', label: 'options.mcDa' },
    { value: 'mc-ma', label: 'options.mcMa' },
  ],
  referenceChart: [
    { value: 'intergrowth-21st', label: 'options.intergrowth21st' },
    { value: 'who', label: 'options.whoFetalGrowth' },
    { value: 'snijders-1994', label: 'options.snijders1994' },
  ],
} as const satisfies Record<string, readonly SelectOption[]>;

export type AmnioticFluidValue =
  (typeof OPTIONS.amnioticFluid)[number]['value'];
export type BaselineFhrValue = (typeof OPTIONS.baselineFhr)[number]['value'];

export function amnioticFluidFromSdpCm(
  sdpCm?: number | null
): AmnioticFluidValue | undefined {
  if (sdpCm == null || !Number.isFinite(sdpCm)) {
    return undefined;
  }
  if (sdpCm < 2) {
    return 'OLIGOHYDRAMNIOS';
  }
  if (sdpCm < 8) {
    return 'NORMAL';
  }
  return 'POLYHYDRAMNIOS';
}

export function baselineFhrFromHeartRateBpm(
  heartRateBpm?: number | null
): BaselineFhrValue | undefined {
  if (heartRateBpm == null || !Number.isFinite(heartRateBpm)) {
    return undefined;
  }
  if (heartRateBpm < 110) {
    return 'BRADYCARDIA';
  }
  if (heartRateBpm <= 160) {
    return 'NORMAL';
  }
  return 'TACHYCARDIA';
}

export const EFW_FORMULA_FIELD_MAP: Record<string, string> = {
  'hadlock-i': 'efw_hadlock_i_g', // AC + FL
  'hadlock-ii': 'efw_hadlock_ii_g', // BPD + AC + FL
  'hadlock-iii': 'efw_hadlock_iii_g', // HC + AC + FL
  'hadlock-iv': 'efw_hadlock_iv_g', // BPD + HC + AC + FL
  intergrowth: 'efw_who_2017_g', // INTERGROWTH-21st (AC + FL)
};

// ─── Biometry configs per trimester ──────────────────────────────

export const T1_BIOMETRY: BiometryField[] = [
  { key: 'crl', label: 'biometry.crl', showPercentile: false },
  { key: 'nt', label: 'biometry.nt', showPercentile: false },
  { key: 'bpd', label: 'biometry.bpd', showPercentile: false },
];

export const T2_BIOMETRY: BiometryField[] = [
  { key: 'bpd', label: 'biometry.bpd', showPercentile: true },
  { key: 'hc', label: 'biometry.hc', showPercentile: true },
  { key: 'ac', label: 'biometry.ac', showPercentile: true },
  { key: 'fl', label: 'biometry.fl', showPercentile: true },
  { key: 'hl', label: 'biometry.hl', showPercentile: true },
  { key: 'tcd', label: 'biometry.tcd', showPercentile: false },
  { key: 'tibia', label: 'biometry.tibia', showPercentile: false },
  { key: 'fibula', label: 'biometry.fibula', showPercentile: false },
  { key: 'radius', label: 'biometry.radius', showPercentile: false },
  { key: 'ulna', label: 'biometry.ulna', showPercentile: false },
  { key: 'foot', label: 'biometry.footLength', showPercentile: false },
  { key: 'iod', label: 'biometry.iod', showPercentile: false },
  { key: 'nasal', label: 'biometry.nasalBone', showPercentile: false },
];

export const T2_BRAIN_MEASUREMENTS: BiometryField[] = [
  { key: 'lat-vent', label: 'biometry.latVentricle', showPercentile: false },
  { key: 'csp', label: 'biometry.cspWidth', showPercentile: false },
  { key: 'cisterna', label: 'biometry.cisternaMagna', showPercentile: false },
  { key: '3rd-vent', label: 'biometry.thirdVentricle', showPercentile: false },
  { key: 'vermis', label: 'biometry.vermisHeight', showPercentile: false },
  { key: 'choroid', label: 'biometry.choroidPlexus', showPercentile: false },
];

export const T3_BIOMETRY: BiometryField[] = [
  { key: 'bpd', label: 'biometry.bpd', showPercentile: true },
  { key: 'hc', label: 'biometry.hc', showPercentile: true },
  { key: 'ac', label: 'biometry.ac', showPercentile: true },
  { key: 'fl', label: 'biometry.fl', showPercentile: true },
  { key: 'cerebellum', label: 'biometry.cerebellum', showPercentile: true },
];

// ─── Morphology checklists ───────────────────────────────────────

export const T1_MORPHOLOGY: MorphologyItem[] = [
  { key: 'cranial-vault', label: 'morphology.cranialVaultAcrania' },
  { key: 'choroid-plexus', label: 'morphology.choroidPlexus' },
  { key: 'face', label: 'morphology.faceFacialProfile' },
  { key: 'heart-4ch', label: 'morphology.heart4Chamber' },
  { key: 'stomach', label: 'morphology.stomach' },
  { key: 'abd-wall', label: 'morphology.abdominalWall' },
  { key: 'kidneys-bladder', label: 'morphology.kidneysBladder' },
  { key: '4-limbs', label: 'morphology.fourLimbsPresent' },
  { key: 'spine', label: 'morphology.spine' },
  { key: 'midline-csp', label: 'morphology.midlineCsp' },
];

export const T2_MORPHOLOGY: MorphologyItem[] = [
  { key: 'cranial-vault', label: 'morphology.cranialVault' },
  { key: 'brain', label: 'morphology.brainStructures' },
  { key: 'face-profile', label: 'morphology.faceProfile' },
  { key: 'upper-lip', label: 'morphology.upperLip' },
  { key: 'nostrils', label: 'morphology.nostrils' },
  { key: 'mandible', label: 'morphology.mandible' },
  { key: 'thorax', label: 'morphology.thorax' },
  { key: 'diaphragm', label: 'morphology.diaphragm' },
  { key: 'abd-wall', label: 'morphology.abdominalWall' },
  { key: 'cord-insertion', label: 'morphology.cordInsertion' },
  { key: 'liver', label: 'morphology.liverEchogenicity' },
  { key: 'bowel', label: 'morphology.bowelEchogenicity' },
  { key: 'kidneys', label: 'morphology.kidneysBilateral' },
  { key: 'corticomedullary', label: 'morphology.corticomedullaryDiff' },
  { key: 'spine-integrity', label: 'morphology.spineIntegrity' },
  { key: 'vertebral', label: 'morphology.vertebralAlignment' },
  { key: 'posterior-arch', label: 'morphology.posteriorArchClosure' },
  { key: '4-limbs', label: 'morphology.fourLimbsPresent' },
  { key: 'foot-position', label: 'morphology.footPosition' },
  { key: 'upper-limb', label: 'morphology.upperLimbContinuity' },
  { key: 'lower-limb', label: 'morphology.lowerLimbContinuity' },
  { key: 'genitalia', label: 'morphology.externalGenitalia' },
];

// ─── GA defaults per trimester ───────────────────────────────────

export const GA_DEFAULTS: Record<Trimester, string> = {
  '1st': '12',
  '2nd': '22',
  '3rd': '34',
};
